
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { OrderStatus, useOrders } from "@/hooks/use-orders";
import { formatCurrency } from "@/lib/utils";
import { Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";

const getStatusColor = (status: OrderStatus) => {
	switch (status) {
		case "pending":
			return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
		case "received":
			return "bg-blue-100 text-blue-800 hover:bg-blue-100";
		case "processing":
			return "bg-purple-100 text-purple-800 hover:bg-purple-100";
		case "shipped":
			return "bg-green-100 text-green-800 hover:bg-green-100";
		case "delivered":
			return "bg-green-500 text-white hover:bg-green-600";
		case "cancelled":
			return "bg-red-100 text-red-800 hover:bg-red-100";
		default:
			return "bg-gray-100 text-gray-800 hover:bg-gray-200";
	}
};

export function OrderTable() {
	const { orders, isLoading } = useOrders();
	const { isSupplier } = useAuth();
	const navigate = useNavigate();

	if (isLoading) {
		return <div className="py-10 text-center">Loading orders...</div>;
	}

	if (orders.length === 0) {
		return (
			<div className="py-10 text-center">
				<p className="text-gray-500">No orders found.</p>
				{!isSupplier && (
					<Button
						className="mt-4"
						onClick={() => navigate("/products")}>
						Browse Products
					</Button>
				)}
			</div>
		);
	}

	return (
		<div className="rounded-md border">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Order ID</TableHead>
						<TableHead>Date</TableHead>
						<TableHead>Total</TableHead>
						<TableHead>Status</TableHead>
						<TableHead className="text-right">Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{orders.map((order) => (
						<TableRow key={order.id}>
							<TableCell className="font-medium">
								#{order.id.substring(0, 8)}
							</TableCell>
							<TableCell>
								{new Date(
									order.created_at
								).toLocaleDateString()}
							</TableCell>
							<TableCell>
								{formatCurrency(order.amount)}
							</TableCell>
							<TableCell>
								<Badge
									className={getStatusColor(
										order.status as OrderStatus
									)}>
									{order.status}
								</Badge>
							</TableCell>
							<TableCell className="text-right">
								<Button
									variant="ghost"
									size="sm"
									onClick={() =>
										navigate(
											`/dashboard/orders/${order.id}`
										)
									}>
									<Eye className="mr-2 h-4 w-4" />
									View
								</Button>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
